import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Paper, Box, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Button, 
  CircularProgress, Card, CardContent, Divider,
  IconButton, Tooltip, Stack, Chip
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { getTeacherTimetables, getTeacherTimetable } from '../services/api';

// Function to format time from 24-hour to 12-hour format with AM/PM
const formatTimeSlot = (timeSlot) => {
  if (!timeSlot) return '';
  
  // Split the time slot string into start and end times
  const [startTime, endTime] = timeSlot.split(' - ');
  
  // Format start time
  const formattedStartTime = formatTime(startTime);
  
  // Format end time
  const formattedEndTime = formatTime(endTime);
  
  return `${formattedStartTime} - ${formattedEndTime}`;
};

// Helper function to format a single time string (24-hour "HH:MM[:SS]")
const formatTime = (timeString) => {
  if (!timeString) return '';

  let [hours, minutes] = timeString.split(':').map(Number);

  // The database stores post-lunch slots as 01:30, 02:30, 03:30 instead of 13:30…
  // Detect that pattern: if the hour is 1–3 (inclusive) we interpret it as PM.
  if (hours >= 1 && hours <= 4) {
    hours += 12; // convert to 13,14,15 – proper 24-hour value
  }

  const dateObj = new Date(1970, 0, 1, hours, minutes);

  // Force 12-hour clock with upper-case AM/PM
  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',   // 1-12
    minute: '2-digit', // 00-59
    hour12: true
  });
};

// Function to generate consistent colors based on subject name
const getSubjectColor = (subjectName) => {
  if (!subjectName) return '#1976d2'; // Default blue color

  // Generate a consistent hash from the subject name
  let hash = 0;
  for (let i = 0; i < subjectName.length; i++) {
    hash = subjectName.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert the hash to a hue value (0-360)
  const hue = hash % 360;
  
  // Use vibrant colors but avoid extremely bright or dark ones
  return `hsl(${hue}, 70%, 40%)`; // Saturation at 70%, lightness at 40%
};

// Component to display a list of all teachers with their timetable summary
const TeachersList = () => {
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTeachers();
  }, []);

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const response = await getTeacherTimetables();
      setTeachers(response.data);
    } catch (error) {
      console.error('Error fetching teachers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTimetable = (teacherId) => {
    navigate(`/teacher-timetable/${teacherId}`);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom mt={3}>
        Teachers Timetable
      </Typography>
      <Typography variant="body1" paragraph>
        Select a teacher to view their complete timetable across all departments and semesters.
      </Typography>

      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><Typography variant="subtitle1" fontWeight="bold">Teacher Name</Typography></TableCell>
              <TableCell><Typography variant="subtitle1" fontWeight="bold">Teaching Hours</Typography></TableCell>
              <TableCell><Typography variant="subtitle1" fontWeight="bold">Departments</Typography></TableCell>
              <TableCell><Typography variant="subtitle1" fontWeight="bold">Semesters</Typography></TableCell>
              <TableCell><Typography variant="subtitle1" fontWeight="bold">Actions</Typography></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {teachers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">No teachers found with timetable data</TableCell>
              </TableRow>
            ) : (
              teachers.map((teacher) => (
                <TableRow key={teacher.teacher_id} hover>
                  <TableCell>{teacher.name}</TableCell>
                  <TableCell>{teacher.total_teaching_hours}</TableCell>
                  <TableCell>{teacher.departments_count}</TableCell>
                  <TableCell>{teacher.semesters_count}</TableCell>
                  <TableCell>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      size="small"
                      onClick={() => handleViewTimetable(teacher.teacher_id)}
                    >
                      View Timetable
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

// Component to display the detailed timetable for a single teacher
const TeacherTimetableDetail = () => {
  const { teacherId } = useParams();
  const [timetable, setTimetable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeSlots, setTimeSlots] = useState([]);
  const navigate = useNavigate();
  
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  useEffect(() => {
    fetchTeacherTimetable();
  }, [teacherId]);

  const fetchTeacherTimetable = async () => {
    try {
      setLoading(true);
      const response = await getTeacherTimetable(teacherId);
      setTimetable(response.data);
      
      /* -------------------------------------------
         Build the list of unique time-slots based on
         the numeric slot id supplied by the backend.
         This guarantees correct ordering (1,2,4,5…)
         even if the underlying start_time strings are
         in 12-hour format or otherwise inconsistent.
      ------------------------------------------- */

      const slotIdToLabel = new Map(); // id -> "start - end"

      // Traverse every scheduled class to collect labels
      Object.values(response.data.timetable).forEach(daySchedule => {
        Object.entries(daySchedule).forEach(([slotIdStr, classes]) => {
          if (!classes || classes.length === 0) return;

          const slotId = Number(slotIdStr);

          // Use the first class of this slot to derive the start-end label.
          const { start_time, end_time } = classes[0];
          const label = `${start_time} - ${end_time}`;

          if (!slotIdToLabel.has(slotId)) {
            slotIdToLabel.set(slotId, label);
          }
        });
      });

      // Fall back to a predefined set for slots that might be empty for this teacher
      const predefinedSlots = [
        [1, '8:30:00 - 9:30:00'],
        [2, '9:30:00 - 10:30:00'],
        [4, '10:45:00 - 11:45:00'],
        [5, '11:45:00 - 12:45:00'],
        [7, '13:30:00 - 14:30:00'],
        [8, '14:30:00 - 15:30:00'],
        [9, '15:30:00 - 16:30:00']
        
      ];

      predefinedSlots.forEach(([id, label]) => {
        if (!slotIdToLabel.has(id)) {
          slotIdToLabel.set(id, label);
        }
      });

      const sortedTimeSlots = Array.from(slotIdToLabel.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([_, label]) => label);

      setTimeSlots(sortedTimeSlots);
      
    } catch (error) {
      console.error('Error fetching teacher timetable:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to convert time to minutes for sorting
  const convertTimeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to find a class at a specific time slot and day
  const findClassForTimeSlot = (day, timeSlot) => {
    if (!timetable || !timetable.timetable[day]) return null;
    
    const [startTime] = timeSlot.split(' - ');
    
    // Check each time slot in the day's schedule
    for (const [, classes] of Object.entries(timetable.timetable[day])) {
      for (const classInfo of classes) {
        if (`${classInfo.start_time} - ${classInfo.end_time}` === timeSlot) {
          return classInfo;
        }
      }
    }
    
    return null;
  };

  // Function to export the timetable to PDF
  const exportToPDF = () => {
    // Create a new PDF document
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    
    // Add a title
    doc.setFontSize(16);
    doc.text(`${timetable.teacher_name}'s Timetable`, doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
    
    // ---------------------------------------------
    //  Build table data
    // ---------------------------------------------
    const tableColumn = ['Time Slot', ...days];
    const tableRows   = [];
    
    // Populate the table rows
    timeSlots.forEach(timeSlot => {
      const row = [formatTimeSlot(timeSlot)];
      
      days.forEach(day => {
        const classInfo = findClassForTimeSlot(day, timeSlot);
        let cellContent = '';
        
        if (classInfo) {
          cellContent = [
            classInfo.subject_name,
            `(${classInfo.subject_code})`,
            `${classInfo.department} - ${classInfo.semester}`,
            `Sec: ${classInfo.section} | ${classInfo.is_lab ? 'Lab' : 'Theory'}`
          ].join('\n');
        }
        
        row.push(cellContent);
      });
      
      tableRows.push(row);
    });
    
    // Generate the table
    autoTable(doc, {
      head       : [tableColumn],
      body       : tableRows,
      startY     : 25,
      theme      : 'grid',          // crisper borders
      styles     : {
        fontSize   : 10,
        fontStyle: 'bold',
        textColor  : [40, 40, 40], // near-black body text
        cellPadding: 3,
        overflow   : 'linebreak',
        valign     : 'middle',
        halign     : 'center',
      },
      columnStyles: {
        0: { fontStyle: 'bold', halign: 'left', cellWidth: 30 } // wider & bold time column
      },
      headStyles : {
        fillColor: [25, 118, 210], // primary blue header
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 11
      },
      alternateRowStyles: { fillColor: [245, 245, 245] }, // light zebra striping
      didParseCell: (hookData) => {
        // Shade LAB cells differently
        if (hookData.section === 'body' && hookData.column.index > 0) {
          const raw = hookData.cell.raw || '';
          if (raw.includes('Lab')) {
            hookData.cell.styles.fillColor = [232, 244, 253]; // light blue
          }
        }
      },
    });
    
    // Save the PDF
    doc.save(`${timetable.teacher_name}_Timetable.pdf`);
  };

  // Function to export the timetable to Excel
  const exportToExcel = async () => {
    if (!timetable) return;

    // Build workbook
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Timetable');

    // Columns (narrower for compact layout)
    ws.columns = [{ header: 'Time Slot', key: 'time', width: 16 }, ...days.map(d => ({ header: d, key: d, width: 26 }))];

    const borderStyle = { style: 'thin', color: { argb: 'FFB0B0B0' } };

    // Header styling
    ws.getRow(1).eachCell(cell => {
      cell.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = { top: borderStyle, left: borderStyle, right: borderStyle, bottom: borderStyle };
    });

    let rowIndex = 2;

    timeSlots.forEach(timeLabel => {
      const rowData = [formatTimeSlot(timeLabel)];
      const labFlags = [];
      days.forEach(dayName => {
        const cls = findClassForTimeSlot(dayName, timeLabel);
        if (cls) {
          const parts = [
            cls.subject_name || cls.subject || '',
            `(${cls.subject_code || ''})`,
            `${cls.department || ''} - ${cls.semester || ''}`,
            `Sec: ${cls.section || ''} | ${cls.is_lab ? 'Lab' : 'Theory'}`
          ];
          rowData.push(parts.filter(Boolean).join('\n'));
          labFlags.push(cls.is_lab || false);
        } else {
          rowData.push('');
          labFlags.push(false);
        }
      });
      ws.addRow(rowData);
      const row = ws.getRow(rowIndex);
      row.height = 70; // taller to display multi-line details without clipping
      if (rowIndex % 2 === 0) {
        row.eachCell(cell => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F7FA' } };
        });
      }
      row.eachCell((cell, col) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        if (col === 1) cell.font = { bold: true, size: 12 };
        if (col > 1 && labFlags[col - 2]) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE7F6' } };
        }
        cell.border = { top: borderStyle, left: borderStyle, right: borderStyle, bottom: borderStyle };
      });
      rowIndex++;
    });

    ws.pageSetup = { orientation: 'landscape', paperSize: 9, fitToPage: true, fitToWidth: 1, fitToHeight: 1, horizontalCentered: true };

    const safe = s => s.replace(/[^a-z0-9]/gi, '_');
    const fname = `${safe(timetable.teacher_name)}_Timetable.xlsx`;

    const buffer = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), fname);
  };

  const handleBack = () => {
    navigate('/teacher-timetable');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!timetable) {
    return (
      <Container maxWidth="lg">
        <Box mt={3} display="flex" alignItems="center">
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            No timetable data found for this teacher
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box mt={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <IconButton onClick={handleBack} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">
            {timetable.teacher_name}'s Timetable
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Tooltip title="Export to PDF">
            <Button
              variant="contained"
              color="primary"
              startIcon={<PictureAsPdfIcon />}
              onClick={exportToPDF}
            >
              PDF
            </Button>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <Button
              variant="contained"
              color="success"
              startIcon={<FileDownloadIcon />}
              onClick={exportToExcel}
            >
              Excel
            </Button>
          </Tooltip>
        </Stack>
      </Box>
      
      <Typography variant="body1" paragraph mt={1}>
        Complete teaching schedule across all departments and semesters
      </Typography>

      <Box mt={3}>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer component={Paper} elevation={3} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px', minWidth: 650 }}>
            <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#1976d2' }}>
                  <TableCell width="12%" sx={{ fontWeight: 'bold', color: '#fff', borderRight: '1px solid #e0e0e0', borderBottom: '2px solid #e0e0e0' }}>
                    <Typography variant="subtitle1" fontWeight="bold">Time / Day</Typography>
                  </TableCell>
                  {days.map(day => (
                    <TableCell 
                      key={day} 
                      align="center" 
                      sx={{ fontWeight: 'bold', color: '#fff', borderRight: '1px solid #e0e0e0', borderBottom: '2px solid #e0e0e0', '&:last-child': { borderRight: 'none' } }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">{day}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {timeSlots.map((timeSlot, index) => (
                  <TableRow 
                    key={timeSlot} 
                    hover 
                    sx={{ 
                      backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff',
                      '&:last-child td': { borderBottom: 0 } 
                    }}
                  >
                    <TableCell 
                      sx={{ 
                        borderRight: '1px solid #e0e0e0', 
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#f5f5f5' 
                      }}
                    >
                      <Typography variant="body2" fontWeight="bold">
                        {formatTimeSlot(timeSlot)}
                      </Typography>
                    </TableCell>
                    
                    {days.map(day => {
                      const classInfo = findClassForTimeSlot(day, timeSlot);
                      // Calculate a color based on subject name if there's a class info
                      const subjectColor = classInfo ? getSubjectColor(classInfo.subject_name) : null;
                      
                      return (
                        <TableCell 
                          key={`${day}-${timeSlot}`} 
                          align="center"
                          sx={{
                            borderRight: '1px solid #e0e0e0',
                            borderBottom: '1px solid #e0e0e0',
                            padding: '8px',
                            height: '100px',
                            '&:last-child': { borderRight: 'none' },
                            ...(classInfo?.is_lab && { 
                              backgroundColor: 'rgba(232, 244, 253, 0.8)',
                              border: '1px solid #bbdefb' 
                            }),
                            ...(classInfo && !classInfo.is_lab && { 
                              backgroundColor: `${subjectColor}15`, // Light version of the subject color
                              border: `1px solid ${subjectColor}30` 
                            })
                          }}
                        >
                          {classInfo ? (
                            <Box 
                              sx={{ 
                                p: 1, 
                                borderRadius: '4px',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center'
                              }}
                            >
                              <Typography 
                                variant="body2" 
                                fontWeight="bold" 
                                color={classInfo.is_lab ? 'primary' : 'inherit'}
                                sx={{ 
                                  mb: 0.5,
                                  borderBottom: `2px solid ${subjectColor || '#1976d2'}`,
                                  paddingBottom: '2px'
                                }}
                              >
                                {classInfo.subject_name}
                                {classInfo.is_lab && 
                                  <Chip 
                                    size="small" 
                                    label="LAB" 
                                    color="primary"
                                    sx={{ ml: 1, height: '16px', fontSize: '0.6rem' }}
                                  />
                                }
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {classInfo.subject_code}
                              </Typography>
                              <Divider sx={{ my: 0.5 }} />
                              <Box mt={0.5}>
                                <Typography variant="caption" display="block">
                                  {classInfo.department} - {classInfo.semester}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  Section: {classInfo.section}
                                </Typography>
                              </Box>
                            </Box>
                          ) : null}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Container>
  );
};

// Main component that handles routing between list and detail views
const TeacherTimetable = () => {
  const { teacherId } = useParams();
  
  return teacherId ? <TeacherTimetableDetail /> : <TeachersList />;
};

export default TeacherTimetable;
